import { Grid, makeStyles } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import clsx from "clsx";
import moment from "moment";
import PropTypes from "prop-types";
import React, { memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import { DayPickerRangeController } from "react-dates";
import { START_DATE } from "react-dates/constants";
import "react-dates/initialize";
import "../../../../www/css/_datepicker.css";
import { useDate, useI18N } from "../../../common/common";
import { YEAR_MONTH_DAY_FORMAT } from "../../../common/constant";
import { dateToUTCTimezone, getYearMonthDayFormat, weekdaysMap } from "../../../common/date";
import { useBookingPeriod, useBreakpoints, useCompanyDetails } from "../../../hooks";
import { ApartmentCategoryAvailabilityCalendarContext } from "../../../store/providers/ApartmentCategoryAvailabilityCalendarProvider";

const useStyles = makeStyles(() => ({
    height100: {
        height: "100%"
    },
    searchDatesWrapper: {
        overflowX: "hidden",
        overflowY: "auto",
        width: "100%",
        height: "100%"
    },
    datepicker: {
        margin: "0 auto",
        height: "auto !important"
    },
    minHeight: {
        minHeight: "367px"
    }
}));

const calendarDayAvailabilityStatus = {
    Available: "available",
    NonAvailable: "non_available",
    DepartureOnly: "departure_only",
    Blocked: "blocked",
    MinNights: "min_nights",
    Occupied: "occupied"
};

const ApartmentAvailabilityCalendar = ({
    searchData,
    resortDetails,
    apartmentAvailability,
    onSetApartmentsAvailability,
    loadingApartmentAvailability,
    triggerCustomEventUpdateDates,
    numberOfMonthsToShow,
    onNextMonthChanged,
    nextMonthTriggered,
    apartment,
    withOccupiedStatus,
    currentApartmentIndex,
    onSetSelectedCalendarCategoryApartment
}) => {
    const classes = useStyles();
    const {i18n, i18nLowerIfRequired} = useI18N();
    const {isExtraSmallScreen, isSmallScreen} = useBreakpoints();
    const {bookingPeriodEndDate, bookingPeriodDurationInMonths} = useBookingPeriod();
    const {companyDetails} = useCompanyDetails();
    const {formatDate} = useDate();
    const {
        dateFrom,
        setDateFrom,
        dateTo,
        setDateTo,
        focus,
        setFocus,
        handleOpenSnackbar
    } = useContext(ApartmentCategoryAvailabilityCalendarContext);

    const [reachedBookingPeriodBeginning, setReachedBookingPeriodBeginning] = useState(true);
    const [reachedBookingPeriodEnd, setReachedBookingPeriodEnd] = useState(false);
    const [resortDetailsDatePickerCta, setResortDetailsDatePickerCta] = useState(undefined);
    const [companyDetailsDatePickerCta, setCompanyDetailsDatePickerCta] = useState(undefined);
    const [availabilityStatusOfLast10Days, setAvailabilityStatusOfLast10Days] = useState();
    const [visibleMonth, setVisibleMonth] = useState(moment());
    const calendarRef = useRef(null);
    const [isManuallyMonthNavigationTriggered, setIsManuallyMonthNavigationTriggered] = useState(false);

    useEffect(() => {
        if (nextMonthTriggered) {
            setVisibleMonth(moment(nextMonthTriggered?.visibleMonth));

            if (apartment?.apartments_short_code !== nextMonthTriggered?.apartment?.apartments_short_code && nextMonthTriggered?.visibleMonth !== visibleMonth) {
                if (calendarRef.current) {
                    // Prev Month - Right Arrow Button
                    const nextButton = calendarRef.current.querySelector(
                        ".DayPickerNavigation_button__horizontal:nth-child(2)"
                    );

                    // Next Month - Left Arrow Button
                    const prevButton = calendarRef.current.querySelector(
                        ".DayPickerNavigation_button__horizontal:nth-child(1)"
                    );

                    setIsManuallyMonthNavigationTriggered(true);
                    if (moment(nextMonthTriggered?.visibleMonth).isAfter(visibleMonth)) {
                        if (nextButton) {nextButton.click();}
                    } else {
                        if (prevButton) {prevButton.click();}
                    }

                }
            }
        }
    }, [nextMonthTriggered]);

    const isDateFromInLastMonth = useMemo(() => {
        const extendedBookingPeriod = moment(bookingPeriodEndDate)
            .add(10, "d")
            .format(YEAR_MONTH_DAY_FORMAT);

        return dateFrom &&
            (
                moment(dateFrom)?.isSame(bookingPeriodEndDate, "month") ||
                moment(dateFrom)?.isSame(extendedBookingPeriod, "month")
            );
    }, [dateFrom, bookingPeriodEndDate]);

    const numberOfMonths = useMemo(() => {
        if (isExtraSmallScreen()) {
            let monthsToShow = bookingPeriodDurationInMonths + 1;
            // If date from is in the last month prior to the end of the booking period, then show also the next month from the last
            if (isDateFromInLastMonth) {
                monthsToShow += 1;
            }
            return monthsToShow;
        } else if (isSmallScreen()) {
            return 1;
        } else {
            return numberOfMonthsToShow;
        }
    }, [bookingPeriodDurationInMonths, isDateFromInLastMonth, resortDetails, isExtraSmallScreen(), isSmallScreen()]);

    useEffect(() => {
        if (!apartmentAvailability?.length > 0) {
            return;
        }

        const disabledDatesMap = new Map();

        const now = moment();

        const endOfBookingPeriod = moment(dateToUTCTimezone(bookingPeriodEndDate));
        const firstCalendarDay = moment().startOf("month");
        const lastCalendarDay = endOfBookingPeriod.clone().add(1, "months")?.endOf("month");

        // If date from is in the last month prior to the end of the booking period, then extend the booking period by 10 days
        if (!dateTo && moment(dateFrom)?.isSame(endOfBookingPeriod, "month")) {
            onSetApartmentsAvailability(apartmentAvailability
                .filter(day => moment(day.date).isAfter(endOfBookingPeriod))
                .forEach((day) => {
                    day.availability_status = availabilityStatusOfLast10Days.find(item => item.date === day.date)?.availability_status;
                }));
            endOfBookingPeriod.add(10, "d");
        }

        // Disable dates before now
        for (const i = firstCalendarDay; i.isBefore(now.clone().subtract(1, "days")); i.add(1, "days")) {
            disabledDatesMap.set(getYearMonthDayFormat(i), {info: ""});
        }

        // Disable dates after end of booking period
        for (const i = lastCalendarDay; i.isAfter(endOfBookingPeriod.clone().add(1, "days")); i.subtract(1, "days")) {
            disabledDatesMap.set(getYearMonthDayFormat(i), {info: getBookingPeriodEndLabel()});
        }

        // Disable closing times dates
        resortDetails?.closingTimesCollection?.items.forEach((times) => {
            const from = moment(dateToUTCTimezone(times?.from?.split("T")[0]));
            const fromPlusOneDay = from.add(1, "days");
            const to = moment(dateToUTCTimezone(times?.to?.split("T")[0]));
            const toPlusOneDay = to.add(1, "days");
            setResortDetailsDatePickerCta({...times?.datePickerCta});
            for (let i = fromPlusOneDay; i.isBefore(toPlusOneDay); i.add(1, "days")) {
                disabledDatesMap.set(getYearMonthDayFormat(i), {info: times?.information});
            }
        });

        // Disable weekdays with arrival restrictions
        if (resortDetails?.arrivalRestrictions?.weekdaysWithArrivalRestrictions?.length > 0) {
            const daysArray = resortDetails?.arrivalRestrictions?.weekdaysWithArrivalRestrictions?.map((theDay) => {
                return weekdaysMap[theDay];
            });

            for (let i = firstCalendarDay; i.isBefore(lastCalendarDay); i.add(1, "days")) {
                if (daysArray.includes(i.day())) {
                    disabledDatesMap.set(getYearMonthDayFormat(i), {info: ""});
                }
            }
        }

        // Check and load min stay restrictions
        if (resortDetails?.resortStayRestrictions?.length) {
            // If there is a selected day (dateFrom), then execute the functionality to disable the days that are restricted after the selected day
            if (dateFrom) {
                const selectedDay = moment(dateFrom);
                const matchedStayRestriction = resortDetails.resortStayRestrictions.find(({dateFrom, dateTo}) => selectedDay.isBetween(moment(dateFrom), moment(dateTo), undefined, "[]"));

                const minNights = matchedStayRestriction.minNights;
                if (minNights) {
                    for (let i = 1; i < minNights; i++) {
                        const nextDay = moment(selectedDay);
                        nextDay.add(i, "d");
                        disabledDatesMap.set(getYearMonthDayFormat(nextDay), {
                            info: minNightRestrictionInfo(minNights),
                            minNights: true,
                            isStayRestrictedDay: true
                        });
                    }
                }
            } else {
                // If there is no selected day (dateFrom), then execute the functionality to show the restricted days in the calendar upfront for each stay restriction
                for (const restriction of resortDetails.resortStayRestrictions) {
                    const stayRestrictionDayRangeFromApartmentAvailability = apartmentAvailability
                        .filter(({ date }) => {
                            const day = moment(date);
                            return day.isSameOrAfter(moment(getYearMonthDayFormat(restriction.dateFrom))) && day.isSameOrBefore(restriction.dateTo);
                        });

                    const minNights = restriction.minNights;
                    if (minNights) {
                        stayRestrictionDayRangeFromApartmentAvailability.forEach(({date, availability_status}) => {
                            if (availability_status === calendarDayAvailabilityStatus.Available && !disabledDatesMap.has(date)) {
                                for (let i = 1; i < minNights; i++) {
                                    const nextDate = moment(date);
                                    nextDate.add(i, "d");

                                    const nextDayAvailabilityStatus = stayRestrictionDayRangeFromApartmentAvailability.find(item => item.date === getYearMonthDayFormat(nextDate))?.availability_status;

                                    if (nextDayAvailabilityStatus && nextDayAvailabilityStatus !== calendarDayAvailabilityStatus.Available) {
                                        disabledDatesMap.set(getYearMonthDayFormat(date), {
                                            info: minNightRestrictionInfo(minNights),
                                            minNights: true
                                        });
                                    }
                                }
                            }
                        });
                    }
                }
            }
        }

        let availabilityStatusOfLast10DaysOfLastExtraMonthArray = [];
        for (const [date, {info, minNights, isStayRestrictedDay}] of disabledDatesMap.entries()) {
            const dayToUpdate = apartmentAvailability.find(item => item.date === date);

            if (dayToUpdate) {
                if (moment(dayToUpdate.date).isAfter(endOfBookingPeriod)) {
                    const copy = JSON.parse(JSON.stringify(dayToUpdate));
                    availabilityStatusOfLast10DaysOfLastExtraMonthArray.push(copy);
                }

                dayToUpdate.initial_availability_status = dayToUpdate.availability_status;
                dayToUpdate.availability_status = minNights ? calendarDayAvailabilityStatus.MinNights : calendarDayAvailabilityStatus.Blocked;
                dayToUpdate.info = info;
                dayToUpdate.isStayRestrictedDay = isStayRestrictedDay ?? false;
            }
        }
        setAvailabilityStatusOfLast10Days(availabilityStatusOfLast10DaysOfLastExtraMonthArray);
    }, [
            resortDetails?.closingTimesCollection?.items?.length,
            resortDetails?.arrivalRestrictions?.weekdaysWithArrivalRestrictions?.length,
            bookingPeriodEndDate,
            dateFrom,
            apartmentAvailability?.length,
            currentApartmentIndex
    ]);

    useEffect(() => {
        if (isExtraSmallScreen() && apartmentAvailability) {
            if (dateFrom && dateTo) {
                setTimeout(() => {
                    const calendarDaySelectedStart = document.querySelectorAll(".CalendarDay__selected_end");
                        calendarDaySelectedStart?.[0]?.scrollIntoView({
                            block: "end",
                            inline: "nearest",
                            behavior: "smooth"
                        });
                }, 500);
            } else if (searchData?.dateFrom) {
                const diff = moment(searchData?.dateFrom)?.startOf("month").diff(moment().startOf("month"), "months");
                setTimeout(() => {
                    const calendarMonths = document.querySelectorAll(".CalendarMonth");
                        calendarMonths?.[diff]?.scrollIntoView({block: "end", inline: "nearest", behavior: "smooth"});
                }, 500);
            }
        }
    }, [apartmentAvailability?.length]); // Here we don't want dateFrom or searchData?.dateFrom as dependencies

    useEffect(() => {
        if (companyDetails?.datePickerCta) {
            setCompanyDetailsDatePickerCta(companyDetails.datePickerCta);
        }
    }, [companyDetails?.datePickerCta?.label, companyDetails?.datePickerCta?.url]);

    useEffect(() => {
        if (isExtraSmallScreen()) {
                document.getElementsByClassName("DayPicker_transitionContainer")[0]?.addEventListener("scroll", handleScroll, {passive: true});
                document.getElementsByClassName("CalendarMonthGrid")[0]?.addEventListener("scroll", handleScroll, {passive: true});
        }
        return () => {
                document.getElementsByClassName("DayPicker_transitionContainer")[0]?.removeEventListener("scroll", handleScroll);
                document.getElementsByClassName("CalendarMonthGrid")[0]?.removeEventListener("scroll", handleScroll);
        };
    }, [apartmentAvailability?.length]);

    useEffect(() => {
        if (isExtraSmallScreen() && isDateFromInLastMonth) {
            setTimeout(() => {
                const calendarMonths = document.querySelectorAll(".CalendarMonth");
                    calendarMonths?.[calendarMonths?.length - 1]?.scrollIntoView({
                        block: "end",
                        inline: "nearest",
                        behavior: "smooth"
                    });
            }, 500);
        }
    }, [isDateFromInLastMonth]);

    const updateAvailabilityStatus = (day, status) => {
        day.availability_status = status;
    };

    useEffect(() => {
        generateTheAvailabilityStatusRange(dateFrom, dateTo);
    }, [dateFrom, dateTo]);

    const [trigger, setTrigger] = useState(false);
    useEffect(() => {
        if ((isExtraSmallScreen() || isSmallScreen()) && dateFrom) {
            generateTheAvailabilityStatusRange(dateFrom, null);
            setTrigger(true);
        }
    }, [currentApartmentIndex]);

    useEffect(() => {
        if ((isExtraSmallScreen() || isSmallScreen()) && trigger) {
            generateTheAvailabilityStatusRange(dateFrom, dateTo);
            setTrigger(false);
        }
    }, [apartmentAvailability]);

    const generateTheAvailabilityStatusRange = (dayFrom, dayTo) => {
        if (!dayFrom) {
            setFocus("startDate");
            return;
        }

        const startIndex = apartmentAvailability.findIndex(day => day.date === dayFrom);
        if (startIndex === -1) {
            return;
        }

        if (dayTo && triggerCustomEventUpdateDates) {
            triggerCustomEventUpdateDates();
        }

        let availabilityCalendarRange = new Set();

        // Calculate the availability range and set the corresponding availability status
        for (let i = startIndex; i < apartmentAvailability.length; i++) {
            let day = apartmentAvailability[i];
            let momentDayDate = moment(day.date);
            availabilityCalendarRange.add(day.date);

            // Stop when reaching a date beyond dayTo
            if (dayTo) {
                availabilityCalendarRange.delete(day.date);
                if (momentDayDate.isAfter(dayTo)) {
                    break;
                }
                if (withOccupiedStatus) {
                    if (momentDayDate.isSame(dayTo) && day.initial_availability_status === calendarDayAvailabilityStatus.DepartureOnly) {
                        break;
                    }
                    if (momentDayDate.isSameOrBefore(dayTo)) {
                        const isInvalidBehavior = day.initial_availability_status !== calendarDayAvailabilityStatus.Available && day.initial_availability_status !== calendarDayAvailabilityStatus.Blocked && day.availability_status !== calendarDayAvailabilityStatus.Blocked;
                        if (day.isInAvailabilityRange && isInvalidBehavior && !day.isStayRestrictedDay || !day.isInAvailabilityRange && isInvalidBehavior) {
                            updateAvailabilityStatus(day, calendarDayAvailabilityStatus.Occupied);
                        }
                        availabilityCalendarRange.add(day.date);
                        continue;
                    }
                }
                break;
            }

            if (!resortDetails?.houseboat && !dayTo) {
                if (day.availability_status === calendarDayAvailabilityStatus.MinNights) {
                    if (!day.isStayRestrictedDay) {
                        updateAvailabilityStatus(day, calendarDayAvailabilityStatus.Available);
                        if (withOccupiedStatus && momentDayDate.isSame(dayFrom)) {
                            updateAvailabilityStatus(day, calendarDayAvailabilityStatus.Occupied);
                            break;
                        }
                        continue;
                    } else {
                        if (withOccupiedStatus && day.initial_availability_status === calendarDayAvailabilityStatus.DepartureOnly) {
                            availabilityCalendarRange.delete(day.date);
                            break;
                        }
                    }
                }

                if (withOccupiedStatus && momentDayDate.isSame(dayFrom) && day.availability_status === calendarDayAvailabilityStatus.NonAvailable) {
                    updateAvailabilityStatus(day, calendarDayAvailabilityStatus.Occupied);
                    break; // NonAvailable is always the last day in the availability range with occupied status
                }

                if (day.availability_status === calendarDayAvailabilityStatus.DepartureOnly) {
                    updateAvailabilityStatus(day, calendarDayAvailabilityStatus.Available);
                    if (withOccupiedStatus && momentDayDate.isSame(dayFrom)) {
                        updateAvailabilityStatus(day, calendarDayAvailabilityStatus.Occupied);
                    }
                    break; // DepartureOnly is always the last day in the availability range
                }
            }

            if (resortDetails?.houseboat && (!dayTo || (isExtraSmallScreen() || isSmallScreen()))) {
                // If the start day is not the same as the next day, then continue the loop with the status nonavailable
                if (day.availability_status === calendarDayAvailabilityStatus.Available && !isSameDayOutsideOfArrivalRestrictions(day.date)) {
                    updateAvailabilityStatus(day, calendarDayAvailabilityStatus.NonAvailable);
                    continue;
                }

                if (day.availability_status === calendarDayAvailabilityStatus.Blocked || day.availability_status === calendarDayAvailabilityStatus.NonAvailable) {
                    // If the started day is the same as the next day then break the loop with the availability status that has already
                    if (isSameDayOutsideOfArrivalRestrictions(day.date)) {
                        if (withOccupiedStatus && momentDayDate.isSame(dayFrom)) {
                            updateAvailabilityStatus(day, calendarDayAvailabilityStatus.Occupied);
                        }
                        break;
                    }

                    // If the status is blocked or nonavailable and the next day is not the same with the started day then continue the loop
                    // because the availability range may have another available day
                    continue;
                }

                if (day.availability_status === calendarDayAvailabilityStatus.DepartureOnly) {
                    updateAvailabilityStatus(day, calendarDayAvailabilityStatus.Available);

                    // If the started day is the same as the next day then break the loop with the availability status that has already
                    // Else if the next day is not the same as the started day then set the availability status as non available and continue the loop
                    if (isSameDayOutsideOfArrivalRestrictions(day.date)) {
                        if (withOccupiedStatus && momentDayDate.isSame(dayFrom)) {
                            updateAvailabilityStatus(day, calendarDayAvailabilityStatus.Occupied);
                        }
                        break;
                    }
                    updateAvailabilityStatus(day, calendarDayAvailabilityStatus.NonAvailable);
                    continue;
                }
            }
        }

        onSetApartmentsAvailability(
            apartmentAvailability.map(day => {
                const isInAvailabilityRange = availabilityCalendarRange.has(day.date);
                return {
                    ...day,
                    availability_status: isInAvailabilityRange || day.availability_status === calendarDayAvailabilityStatus.Blocked
                        ? day.availability_status
                        : calendarDayAvailabilityStatus.NonAvailable,
                    info: !isInAvailabilityRange
                        ? i18n("view.resort.apartment_category_availability_dialog.this_date_is_not_available")
                        : day.info,
                    isInAvailabilityRange: isInAvailabilityRange
                };
            })
        );
    };

    const minNightRestrictionInfo = (minNightsStayRestriction) => {
        return `${minNightsStayRestriction}-${minNightsStayRestriction > 1 ?
            i18nLowerIfRequired("common.label.nights") :
            i18nLowerIfRequired("common.label.night")} ${i18nLowerIfRequired("common.label.minimum")}`;
    };

    const handleSetPickerDate = (dateType, dateProp) => {
        if ((typeof dateType).toLowerCase() === "string") {
            return dateType ? (dateType[dateProp] ? dateType[dateProp] : moment(dateToUTCTimezone(dateType))) : dateType;
        }
        return dateType ? (dateType[dateProp] ? dateType[dateProp] : moment(dateType)) : dateType;
    };

    const handleChangeDate = (startDate, endDate) => {
        // Remove Date To if we have a houseboat and if date from & date to are not the same week day
        let removeDateToFlag = false;
        if (resortDetails?.houseboat && startDate?.day() !== endDate?.day()) {
            removeDateToFlag = true;
        }
        if ((dateTo === dateFrom) && (endDate && getYearMonthDayFormat(moment(endDate)) === dateTo)) {
            setDateTo(undefined);
            handleChangeDate(getYearMonthDayFormat(moment(startDate)), undefined);
        } else {
            setDateFrom(startDate ? getYearMonthDayFormat(moment(startDate)) : undefined);

            if (endDate && !removeDateToFlag) {
                setDateTo(getYearMonthDayFormat(moment(endDate)));
            } else {
                setDateTo(undefined);
            }
        }
    };

    const handleOnFocus = (focus) => {
        // After clicking reset dates we double check for undefined start_date
        if (!dateFrom && dateTo || !dateFrom && !dateTo) {
            setFocus("endDate");
        } else if (dateTo && dateFrom) {
            setFocus("endDate");
        } else {
            setFocus(!focus ? START_DATE : focus);
        }

        if (onSetSelectedCalendarCategoryApartment) {
            onSetSelectedCalendarCategoryApartment(apartment);
        }
    };

    const isDayBlocked = (day) => {
        const matchedDayAvailabilityStatus = apartmentAvailability?.find(item => item.date === getYearMonthDayFormat(day))?.availability_status;
        if (matchedDayAvailabilityStatus !== calendarDayAvailabilityStatus.Available ||
                (matchedDayAvailabilityStatus === calendarDayAvailabilityStatus.Available && dateTo && dateFrom)
        ) {
            return true;
        }

        if (dateFrom && !dateTo && resortDetails?.houseboat) {
            !isSameDayOutsideOfArrivalRestrictions(day);
        }
    };

    const isSameDayOutsideOfArrivalRestrictions = (day) => {
        const daysArray = resortDetails?.arrivalRestrictions?.weekdaysWithArrivalRestrictions?.map((theDay) => {
            return weekdaysMap[theDay];
        });
        const dayFrom = moment(dateFrom);
        const convertedDay = moment(day);
        return !daysArray?.includes(dayFrom.day()) && convertedDay.day() === dayFrom.day();
    };

    const handleMonthClick = (item) => {
        if (!isManuallyMonthNavigationTriggered && apartment) {
            onNextMonthChanged(item, apartment);
        }
        setReachedBookingPeriodBeginning(moment().startOf("month")?.isSame(item.startOf("month")));

        const lastDisplayMonth = getYearMonthDayFormat(item.add(numberOfMonths - 2, "months").endOf("month")); // -2 because we want to get an extra month displayed, after the last one
        const hasReachedBookingPeriodEnd = bookingPeriodEndDate === lastDisplayMonth;
        setReachedBookingPeriodEnd(hasReachedBookingPeriodEnd);
        if (hasReachedBookingPeriodEnd) {
            handleOpenSnackbar(getBookingPeriodEndLabel(), companyDetailsDatePickerCta);
        }
        setIsManuallyMonthNavigationTriggered(false);
    };

    const getBookingPeriodEndLabel = () => {
        return formatDate(bookingPeriodEndDate, {month: "long"}) + " " + i18n("dialog.search_bar.search_resorts.date.snackbar");
    };

    const handleRenderDayContents = (day) => {
        const matchedDay = apartmentAvailability?.find(item => item.date === getYearMonthDayFormat(day));

        return (
            <span
                className={clsx({
                    "disabled-hoverable": matchedDay?.availability_status !== calendarDayAvailabilityStatus.Available ||
                        matchedDay?.availability_status !== calendarDayAvailabilityStatus.MinNights,
                    "disabled-hoverable-single-digit": (matchedDay?.availability_status !== calendarDayAvailabilityStatus.Available ||
                        matchedDay?.availability_status !== calendarDayAvailabilityStatus.MinNights) && parseInt(day.format("D")) < 10,
                    "available": matchedDay?.availability_status === calendarDayAvailabilityStatus.Available,
                    "non_available": matchedDay?.availability_status === calendarDayAvailabilityStatus.NonAvailable,
                    "departure_only": matchedDay?.availability_status === calendarDayAvailabilityStatus.DepartureOnly,
                    "min_nights": matchedDay?.availability_status === calendarDayAvailabilityStatus.MinNights,
                    "blocked": matchedDay?.availability_status === calendarDayAvailabilityStatus.Blocked,
                    "occupied": matchedDay?.availability_status === calendarDayAvailabilityStatus.Occupied,
                })}
                data-blocked={matchedDay?.availability_status !== calendarDayAvailabilityStatus.Available}
                data-blocked-text={matchedDay?.info || (matchedDay?.availability_status === calendarDayAvailabilityStatus.DepartureOnly
                    ? i18n("view.resort.apartment_category_availability_dialog.this_date_is_available_for_checkout_out_only")
                    : i18n("view.resort.apartment_category_availability_dialog.this_date_is_not_available"))
                }

                onClick={disabledDayClick}
            >
                {day.format("D")}
            </span>
        );
    };

    const disabledDayClick = (event) => {
        if (event?.currentTarget.getAttribute("data-blocked") === "true") {
            handleOpenSnackbar(event?.currentTarget.getAttribute("data-blocked-text"), resortDetailsDatePickerCta);
        }
    };

    const handleScroll = (event) => {
        const dateElement = event.target;
        const distanceFromBottom = dateElement.scrollHeight - dateElement.scrollTop - dateElement.offsetHeight;
        if (distanceFromBottom <= 0) {
            setReachedBookingPeriodEnd(true);
            handleOpenSnackbar(getBookingPeriodEndLabel(), companyDetailsDatePickerCta);
        }
    };

    return (
        <Grid
            className={clsx(classes.height100, classes.minHeight)}
            container
            onScroll={handleScroll}
            justify={"center"}
            spacing={loadingApartmentAvailability ? 2 : 0}
        >
            {!loadingApartmentAvailability ? (
                <span
                    className={clsx("categoryAvailabilityDatepicker", {
                        [classes.searchDatesWrapper]: isExtraSmallScreen(),
                        [classes.datepicker]: !isExtraSmallScreen()
                    })}
                >
                    <div
                        ref={calendarRef}
                        className={classes.height100}
                    >
                        <DayPickerRangeController
                            startDate={handleSetPickerDate(dateFrom, "startDate")}
                            endDate={handleSetPickerDate(dateTo, "endDate")}
                            onDatesChange={({
                                startDate,
                                endDate
                            }) => handleChangeDate(startDate, endDate)}
                            focusedInput={focus}
                            initialVisibleMonth={() => {
                                const intialDate = (dateFrom || searchData?.dateFrom) ?? undefined;
                                if (!intialDate || isExtraSmallScreen()) {
                                    setReachedBookingPeriodBeginning(true);
                                    setReachedBookingPeriodEnd(false);
                                    return moment();
                                } else {
                                    const dateFromMoment = moment(intialDate);
                                    const isCurrentMonth = moment().isSame(dateFromMoment, "month");
                                    const displayMonth = (resortDetails || isCurrentMonth) ? dateFromMoment : dateFromMoment.subtract(1, "months");

                                    setReachedBookingPeriodBeginning(displayMonth.startOf("month")?.isSame(moment().startOf("month")));

                                    const lastDisplayMonth = getYearMonthDayFormat(displayMonth.clone().add(numberOfMonths - 2, "months")?.endOf("month")); // -2 because we want to get an extra month displayed, after the last one
                                    const hasReachedBookingPeriodEnd = bookingPeriodEndDate === lastDisplayMonth;
                                    setReachedBookingPeriodEnd(hasReachedBookingPeriodEnd);
                                    return displayMonth;
                                }
                            }}
                            onFocusChange={handleOnFocus}
                            numberOfMonths={numberOfMonths}
                            noBorder
                            hideKeyboardShortcutsPanel
                            orientation={isExtraSmallScreen() ? "verticalScrollable" : "horizontal"}
                            firstDayOfWeek={1}
                            isOutsideRange={day => ((dateFrom && !dateTo) ? moment.duration(day.diff(moment(dateFrom))).asDays() > 90 : false)}
                            daySize={isExtraSmallScreen() ? 41 : 45}
                            // Hide arrow navigation
                            navPrev={isExtraSmallScreen() || reachedBookingPeriodBeginning ? (
                                <button hidden>{""}</button>) : null}
                            navNext={isExtraSmallScreen() || reachedBookingPeriodEnd ? (
                                <button hidden>{""}</button>) : null}
                            isDayBlocked={isDayBlocked}
                            onPrevMonthClick={handleMonthClick}
                            onNextMonthClick={handleMonthClick}
                            renderDayContents={(day) => handleRenderDayContents(day)}
                        />
                    </div>
                </span>
            ) : (
                Object.values((isExtraSmallScreen()) ? [1, 2, 3] : isSmallScreen() ? [1] : [1, 2]).map((_item, index) => {
                    return (
                        <Grid
                            item
                            key={index}
                        >
                            <Skeleton
                                variant="rect"
                                width={isExtraSmallScreen() ? 314 : 314}
                                height={isExtraSmallScreen() ? 251 : 311}
                            />
                        </Grid>
                    );
                })
            )}
        </Grid>
    );
};

ApartmentAvailabilityCalendar.defaultProps = {
    withOccupiedStatus: false
};

ApartmentAvailabilityCalendar.propTypes = {
    searchData: PropTypes.object,
    resortDetails: PropTypes.object,
    apartmentAvailability: PropTypes.array,
    onSetApartmentsAvailability: PropTypes.func,
    loadingApartmentAvailability: PropTypes.bool,
    triggerCustomEventUpdateDates: PropTypes.func,
    numberOfMonthsToShow: PropTypes.number,
    onNextMonthChanged: PropTypes.func,
    nextMonthTriggered: PropTypes.object,
    apartment: PropTypes.object,
    withOccupiedStatus: PropTypes.bool,
    currentApartmentIndex: PropTypes.number,
    onSetSelectedCalendarCategoryApartment: PropTypes.func
};

export default memo(ApartmentAvailabilityCalendar);
